import React, { useEffect, useState } from "react";
import { Grid, makeStyles } from "@material-ui/core";
import { IMG_LOGO_WHITE } from "images";
import MatchingDetailsHeader from "./matchingDetailsHeader";
import MatchingDetailsScoreCard from "./matchingDetailsScoreCard";
import {
  getJobTalentInfo,
  updateOverallGrade,
} from "controllers/matchingDetails";
import { useParams } from "react-router";
import MatchingDetailsSummaryCard from "./matchingDetailsSummaryCard";
import MatchingDetailsCard from "./matchingDetailsCard";
import MatchingDetailsAssessmentCard from "./matchingDetailsAssessmentCard";
import MatchingDetailsTimelineCard from "./matchingDetailsTimelineCard";
import { getDateByMonthAndYear } from "utils/date";
import JOB_ICON from "./images/jobIcon.svg";
import EDUCATION_ICON from "./images/educationIcon.svg";
import JOB_ICON_DARK from "./images/jobIcon-dark.svg";
import EDUCATION_ICON_DARK from "./images/educationIcon-dark.svg";
import { useThemeMode } from "./useThemeMode";
import { ThemeModeSwitch } from "./ThemeModeSwitch";
import MatchingDetailsChatScript from "./matchingDetailsChatScript";
import { NoData } from "./noData";

const useStyles = makeStyles((theme) => ({
  topBar: {
    height: 64,
    backgroundColor: (props) =>
      theme.palette.matchingDetailsPage[props.mode].textPrimary,
    alignContent: "center",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logoImg: {
    width: 150,
    height: 20,
    paddingLeft: 48,
  },
  container: {
    height: "calc(100% - 64px)",
    padding: "30px 48px",
    background: (props) => theme.palette.matchingDetailsPage[props.mode].bg,
    alignContent: "flex-start",
  },
  cardStyles: {
    height: 320,
  },
  scrollableCardContainer: {
    overflow: "auto",
    height: "calc(320px - 42px)",
  },
}));

const MatchingDetails = () => {
  const [theme, setTheme] = useThemeMode();
  const mode = theme;
  const classes = useStyles({ mode });
  const [matchingInfo, setMatchingInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const params = useParams();

  const fetchMatchingInfo = async (jobTalentId) => {
    setLoading(true);

    const response = await getJobTalentInfo(jobTalentId);

    setLoading(false);
    if (!response) {
      return false;
    }
    setMatchingInfo(response?.response || {});
  };

  useEffect(() => {
    fetchMatchingInfo(params.jobTalentId);
  }, [params.jobTalentId]);

  const separateByDelimiter = (delimiter, firstValue, secondaValue) => {
    if (secondaValue) {
      return `${firstValue}${delimiter}${secondaValue}`;
    }

    return firstValue;
  };

  const upgradeOverallGrade = async (grade) => {
    await updateOverallGrade(params.jobTalentId, grade);
    fetchMatchingInfo(params.jobTalentId);
  };

  return (
    <>
      <Grid container classes={{ root: classes.topBar }}>
        <img src={IMG_LOGO_WHITE} alt="Senseloaf" className={classes.logoImg} />
        <ThemeModeSwitch
          mode={mode}
          setMode={() =>
            setTheme((prevVal) => (prevVal === "light" ? "dark" : "light"))
          }
        />
      </Grid>
      <Grid container classes={{ root: classes.container }}>
        <Grid item xs={12}>
          <MatchingDetailsHeader
            mode={mode}
            name={matchingInfo?.talentName}
            jobRole={matchingInfo?.currentJobRole}
            location={matchingInfo?.currentLocation}
            email={matchingInfo?.talentEmail}
            phone={matchingInfo?.talentContact}
            linkedinProfile={matchingInfo?.linkedinProfile}
            githubProfile={matchingInfo?.githubProfile}
          />
        </Grid>
        <Grid item xs={12}>
          <MatchingDetailsScoreCard
            mode={mode}
            overallScore={matchingInfo?.scores?.overall_score?.score}
            overallScoreGrade={matchingInfo?.scores?.overall_score?.grade}
            workHistoryInsights={
              matchingInfo?.scores?.experience?.insights || []
            }
            workHistoryScore={matchingInfo?.scores?.experience?.score}
            workHistoryGrade={matchingInfo?.scores?.experience?.grade}
            skillInsights={matchingInfo?.scores?.skills?.insights || []}
            skillScore={matchingInfo?.scores?.skills?.score}
            skillGrade={matchingInfo?.scores?.skills?.grade}
            jobTitleInsights={matchingInfo?.scores?.job_title?.insights || []}
            jobTitleScore={matchingInfo?.scores?.job_title?.score}
            jobTitleGrade={matchingInfo?.scores?.job_title?.grade}
            assesmentInsights={matchingInfo?.scores?.assessment?.insights || []}
            assesmentScore={matchingInfo?.scores?.assessment?.score}
            assesmentGrade={matchingInfo?.scores?.assessment?.grade}
            chatPreScreeningInsights={
              matchingInfo?.scores?.chatPreScreening?.insights || []
            }
            chatPreScreeningScore={
              matchingInfo?.scores?.chatPreScreening?.score
            }
            chatPreScreeningGrade={
              matchingInfo?.scores?.chatPreScreening?.grade
            }
            skillsAssessmentScore={
              matchingInfo?.scores?.skillsAssessment?.score
            }
            skillsAssessmentGrade={
              matchingInfo?.scores?.skillsAssessment?.grade
            }
            skillsAssessmentInsights={
              matchingInfo?.scores?.skillsAssessment?.insights || []
            }
            upgradeOverallGrade={upgradeOverallGrade}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <MatchingDetailsCard
                title={"Work History"}
                rootClass={classes.cardStyles}
                mode={mode}
              >
                <div className={classes.scrollableCardContainer}>
                  {matchingInfo?.employments?.length ? (
                    matchingInfo?.employments?.map(
                      ({
                        jobTitle,
                        company,
                        location,
                        startMonth,
                        startYear,
                        endMonth,
                        endYear,
                      }) => (
                        <MatchingDetailsTimelineCard
                          key={jobTitle + company}
                          iconImg={mode === "dark" ? JOB_ICON_DARK : JOB_ICON}
                          title={jobTitle}
                          subTitle={separateByDelimiter(
                            " | ",
                            company,
                            separateByDelimiter(
                              " - ",
                              getDateByMonthAndYear(startMonth, startYear),
                              getDateByMonthAndYear(endMonth, endYear)
                            )
                          )}
                          description={location}
                          mode={mode}
                        />
                      )
                    )
                  ) : (
                    <NoData mode={mode} />
                  )}
                </div>
              </MatchingDetailsCard>
            </Grid>
            <Grid item xs={6}>
              <MatchingDetailsCard
                title={"Education"}
                rootClass={classes.cardStyles}
                mode={mode}
              >
                <div className={classes.scrollableCardContainer}>
                  {matchingInfo?.educations?.length ? (
                    matchingInfo?.educations?.map(
                      (
                        {
                          degree,
                          collegeName,
                          major,
                          location,
                          startMonth,
                          startYear,
                          endMonth,
                          endYear,
                        },
                        index
                      ) => (
                        <MatchingDetailsTimelineCard
                          key={degree + collegeName + major + index}
                          iconImg={
                            mode === "dark"
                              ? EDUCATION_ICON_DARK
                              : EDUCATION_ICON
                          }
                          title={`${degree}${major ? ` (${major})` : ""}`}
                          subTitle={separateByDelimiter(
                            " | ",
                            collegeName,
                            separateByDelimiter(
                              " - ",
                              getDateByMonthAndYear(startMonth, startYear),
                              getDateByMonthAndYear(endMonth, endYear)
                            )
                          )}
                          description={location}
                          mode={mode}
                        />
                      )
                    )
                  ) : (
                    <NoData mode={mode} />
                  )}
                </div>
              </MatchingDetailsCard>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <MatchingDetailsSummaryCard
            mode={mode}
            totalExperience={matchingInfo?.totalWorkExperience}
            avgYearsPerCompany={matchingInfo?.averageWorkExperience}
            highestEducation={matchingInfo?.highestEducation}
            major={matchingInfo?.educations?.[0]?.major}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <MatchingDetailsCard
                loading={loading}
                rootClass={classes.cardStyles}
                mode={mode}
              >
                <MatchingDetailsChatScript
                  mode={mode}
                  applicationId={matchingInfo?.idAtSource}
                  chatTranscript={matchingInfo?.chatTranscript || "{}"}
                />
              </MatchingDetailsCard>
            </Grid>
            <Grid item xs={6}>
              <MatchingDetailsCard
                title={"Assessment"}
                rootClass={classes.cardStyles}
                mode={mode}
              >
                <MatchingDetailsAssessmentCard
                  mode={mode}
                  assessmentScores={matchingInfo?.assessmentScores}
                />
              </MatchingDetailsCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default MatchingDetails;
